/** @jsx jsx */
// CDS
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import toc from 'markdown-toc-unlazy';
import { compose } from 'recompose';

import Retracked from 'js/app/retracked';
import connectToRouter from 'js/lib/connectToRouter';
import user from 'js/lib/user';
import waitForGraphQL from 'js/lib/waitForGraphQL';

import { Grid, breakpoints } from '@coursera/cds-core';

import withNotFound from 'bundles/common/components/withNotFound';
import { CourseraMetatags } from 'bundles/seo';
import type {
  TutorialQuery,
  TutorialQueryVariables,
  TutorialsFieldsFragment,
} from 'bundles/tutorials/__generated__/contentfulTypes';
import Body from 'bundles/tutorials/components/Body';
import ConsumerSignupCTA from 'bundles/tutorials/components/ConsumerSignupCTA';
import Footer from 'bundles/tutorials/components/Footer';
import Header from 'bundles/tutorials/components/Header';
import MobileTableOfContents from 'bundles/tutorials/components/MobileTableOfContents';
import Navigation from 'bundles/tutorials/components/Navigation';
import TableOfContents from 'bundles/tutorials/components/TableOfContents';
import { tutorialsPageQuery } from 'bundles/tutorials/queries';

type TocHeading = {
  content: string;
  slug: string;
  level: number;
};

type PropsFromRouter = {
  preview?: string;
  slug: string;
};

type TutorialPageProps = {
  pageData?: TutorialsFieldsFragment | null;
};

const styles = {
  tutorialPage: css`
    background-color: white;
  `,
  page: () => css`
    max-width: 1300px;
    min-height: 100vh;
    margin: 0 auto;
    background-color: white;
    ${breakpoints.down('md')} {
      position: relative;
      overflow-x: hidden;
      overflow-y: auto;
    }
  `,
  container: () => css`
    max-width: 1000px;
    margin: 0 auto;
    padding: 0 16px;

    ${breakpoints.down('md')} {
      width: 100%;
    }
  `,
  contentContainer: css`
    margin-bottom: 4rem;
  `,
  tableOfContentsContainer: () => css`
    /* stylelint-disable-next-line value-no-vendor-prefix */
    position: -webkit-sticky; /* Safari */
    position: sticky;
    top: 9.5rem;
    max-width: 300px;
    margin: 0 auto;
    align-self: flex-start;
    overflow-y: auto;
    height: min-content;
    ${breakpoints.down('sm')} {
      display: none;
    }
  `,
  mobileTableOfContentsContainer: () => css`
    margin: 20px 0;
    display: none;
    ${breakpoints.down('md')} {
      display: block;
    }
  `,
};

const TutorialPage = ({ pageData }: TutorialPageProps) => {
  if (!pageData) {
    return null;
  }

  const {
    title,
    slug,
    intro,
    bodyText,
    tags,
    previousTutorial,
    nextTutorial,
    featuredImage,
    disableCrawlerIndexing,
    metaTitle,
    metaDescription,
    canonicalUrl,
    sys,
  } = pageData;

  if (!title || !slug || !intro) {
    return null;
  }

  // Table of Content Links are generated from a list of headings with H2 tags
  const tableOfContentsLinks = toc(bodyText)
    .json.filter((heading: TocHeading) => heading.level !== 2)
    .map((heading: TocHeading) => ({
      label: heading.content,
      headingId: heading.slug,
    }));

  const featuredImageUrl = featuredImage?.url;
  const featuredImageCaption = featuredImage?.title;
  const publishedAt = sys?.publishedAt;
  // If this is undefined, someone is seeing this article in preview.
  // In that case, set current date as default.
  const date = publishedAt || new Date().toISOString();
  const authorNames = 'Coursera';

  return (
    <div className="rc-TutorialPage" data-e2e="tutorial-page" css={styles.tutorialPage}>
      <Grid container css={styles.page}>
        <Grid item css={styles.container}>
          <Header
            title={title}
            featuredImage={featuredImage}
            featuredImageCaption={featuredImageCaption}
            subtitle={intro}
            authorNames={authorNames}
            latestPublishedDate={date}
            tags={tags}
          />
          <div css={styles.contentContainer}>
            <div css={styles.mobileTableOfContentsContainer}>
              <MobileTableOfContents links={tableOfContentsLinks} />
            </div>
            <Body bodyText={bodyText} />
          </div>
          <hr />
          <Navigation
            prev={{ route: previousTutorial?.route, label: previousTutorial?.label }}
            next={{ route: nextTutorial?.route, label: nextTutorial?.label }}
          />
          {!!title && <Footer authorNames={authorNames} date={date} title={title} />}
        </Grid>
        <Grid item css={styles.tableOfContentsContainer}>
          <TableOfContents links={tableOfContentsLinks} />
        </Grid>
      </Grid>
      {!user.isAuthenticatedUser() && <ConsumerSignupCTA />}
      <CourseraMetatags
        metaNameAndProperties={{
          title: metaTitle || title,
          ogAndTwitterTitle: metaTitle || title,
          description: metaDescription || intro,
          absoluteImageHref: featuredImageUrl || undefined,
          disableCrawlerIndexing: disableCrawlerIndexing || undefined,
        }}
        paginationProperties={{
          canonicalLinkHrefOverride: canonicalUrl || undefined,
        }}
        hrefLangOverrides={(links) => (disableCrawlerIndexing ? [] : links)}
      />
    </div>
  );
};

export default compose<TutorialPageProps, {}>(
  Retracked.createTrackedContainer(() => ({
    namespace: {
      page: 'tutorial_page',
    },
  })),
  connectToRouter<PropsFromRouter, {}>((router) => ({
    preview: router.location.query.preview,
    slug: router.params.slug,
  })),
  waitForGraphQL<PropsFromRouter, TutorialQuery, TutorialQueryVariables, TutorialPageProps>(tutorialsPageQuery, {
    options: ({ slug, preview }) => {
      return {
        variables: { slug, preview: Boolean(preview) },
        // Only send a request through the Preview API if the user can preview
        context: { clientName: preview ? 'contentfulPreviewGql' : 'contentfulGql' },
        errorPolicy: 'all',
      };
    },
    props: (response) => ({
      pageData: response?.data?.tutorialsCollection?.items?.[0],
    }),
  }),
  withNotFound({ test: ({ pageData }) => !pageData })
)(TutorialPage);
